
import { defineComponent, computed, ref, watch, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElForm } from 'element-plus'
import { AxiosResponse } from 'axios'

import InputGroup from '@/components/layout/InputGroup.vue'
import RequestHeaderLoginWrapper from '@/components/auth-wrappers/RequestHeaderLoginWrapper.vue'
import ApplicantMessageView from '@/components/layout/ApplicantMessageView.vue'
import PushSenderQuestion from '@/components/actions/PushSenderQuestion.vue'
import PushApplicantHistory from '@/components/actions/PushApplicantHistory.vue'
import JrButton from '@/components/jr-parts/JrButton.vue'
import ScheduleAnswerRadioGroup from '@/components/inputs/ScheduleAnswerRadioGroup.vue'
import JrTextarea from '@/components/jr-parts/JrTextarea.vue'
import InputLabel from '@/components/inputs/InputLabel.vue'
import ApplicantScheduleAnswerSend from '@/components/actions/ApplicantScheduleAnswerSend.vue'

import MessageTypeLogic from '@/services/messageTypeLogic'
import RequestHeaderLogic from '@/services/requestHeaderLogic'
import Logger from '@/services/logger'
import { RequestHeaderDetail, ScheduleAnswerApplicantSend, ScheduleAnswerDatePost } from '@/api'
import { scheduleAnswerRule } from '@/constants/rules'
import { ReadRequest as RequestHeaderReadRequest } from '@/requests/requestHeader'
import { MessageTypes } from '@/constants/messageTypes'

/**
 * 日程調整回答
 */
export default defineComponent({
  name: 'ApplicantScheduleMessage',
  components: {
    InputGroup,
    RequestHeaderLoginWrapper,
    ApplicantMessageView,
    PushSenderQuestion,
    PushApplicantHistory,
    JrButton,
    JrTextarea,
    InputLabel,
    ScheduleAnswerRadioGroup,
    ApplicantScheduleAnswerSend,
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const formRef = ref<InstanceType<typeof ElForm>>()
    // 申請情報
    const requestHeaderId = computed(() => {
      const id = Number(route.params.request_header_id)
      return Number.isNaN(id) ? null : id
    })
    // メッセージ種別
    const messageType = computed(() => {
      const type = Number(route.params.message_type)
      return Number.isNaN(type) ? null : type
    })
    // メッセージID
    const messageId = computed(() => {
      const id = Number(route.params.message_id)
      return Number.isNaN(id) ? null : id
    })
    // タイトル
    const title = ref<string>('')
    const scheduleInquiryId = ref<number | null>(null)
    // 送信メッセージ
    const scheduleAnswer = reactive<ScheduleAnswerApplicantSend>({
      body: '',
      schedule_answer_date: [],
    })
    // 送信完了
    const sendComplete = ref<boolean>(false)
    const canAnswer = ref<boolean>(false)

    /**
     * 日付回答の更新
     */
    const updateAnswer = (answers: ScheduleAnswerDatePost[]): void => {
      scheduleAnswer.schedule_answer_date.splice(0)
      scheduleAnswer.schedule_answer_date.push(...answers)
    }

    watch(
      () => [requestHeaderId, messageType, messageId],
      async () => {
        if (route.name !== 'ApplicantScheduleMessage') {
          // 遷移中は何もしない
          return
        }
        // 初期化
        canAnswer.value = false
        title.value = ''
        sendComplete.value = false

        // パラメータチェック
        if (requestHeaderId.value == null || messageType.value == null || messageId.value == null) {
          Logger.info(
            `URLパラメータが不正です 申請基本ID: ${route.params.request_header_id} 種別: ${route.params.message_type} メッセージID: ${route.params.message_id}`
          )
          router.push({ name: 'NotFound' })
          return
        }
        const logic = new MessageTypeLogic(messageType.value, requestHeaderId.value)
        // 開ける種別かどうか確認する
        if (!logic.isScheduleType()) {
          Logger.info(`メッセージ種別が不正です。種別: ${messageType.value}`)
          router.push({ name: 'NotFound' })
          return
        } else if (!logic.isApplicantReceiveType()) {
          // 協議者が受信できるメッセージか確認
          Logger.info(`協議者が受信できないメッセージ種別です。種別: ${messageType.value}`)
          router.push({ name: 'NotFound' })
          return
        }

        // 回答可能かをチェック
        const request = new RequestHeaderReadRequest(requestHeaderId.value)
        if (!(await request.exec())) {
          return
        }
        const { data: requestHeaderData } = request.getResponse() as AxiosResponse<RequestHeaderDetail>
        const requestHeaderLogic = new RequestHeaderLogic(requestHeaderData)
        if (messageType.value === MessageTypes.P_PD_ADJUST_DATE) {
          canAnswer.value = requestHeaderLogic.canAnswerProximityPriorDiscussionSchedule()
        } else if (messageType.value === MessageTypes.P_WS_ADJUST_DATE) {
          canAnswer.value = requestHeaderLogic.canAnswerProximitySchedule()
        } else if (messageType.value === MessageTypes.B_WS_ADJUST_DATE) {
          canAnswer.value = requestHeaderLogic.canAnswerBoundarySchedule()
        }
        Logger.debug(`回答可能: ${canAnswer.value}`)

        // タイトル設定
        title.value = logic.getTypeName()
      },
      { immediate: true, deep: true }
    )

    return {
      formRef,
      title,
      requestHeaderId,
      messageType,
      messageId,
      scheduleAnswer,
      sendComplete,
      scheduleInquiryId,
      updateAnswer,
      rules: scheduleAnswerRule,
      canAnswer,
    }
  },
})
